/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXStatus,
  useStateXStatusDetails,
  useStateXValue,
} from '@cloudio-saas/statex';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { getManager } from 'componentregistry';
import Icon from 'icons/Icon';
import { usePlatform } from 'platform/PlatformHooks';
import React from 'react';
import CodeInput from '../../custom/CodeInput';
import useComponentSize from '../../hooks/useComponentSize';
import {
  getMountedCount,
  getTotalMountedCount,
} from '../../hooks/useComponentTracker';
import { useTheme } from '../../theme/Theme';

export default function AboutPage({
  onClose,
}: {
  onClose: () => void;
}): React.ReactElement | null {
  const platform = usePlatform();
  const page = platform.getPage(platform.appUid, platform.pageId);
  const pageName = useStateXValue(
    ['db', 'IoPages', platform.pageId, 'pageName'],
    '',
  );
  const comps: Record<
    string,
    {
      count: number;
      name: string;
      managerType: 'Trigger' | 'Action' | 'Component';
    }
  > = {};
  const ids = Object.keys(page);
  ids.forEach((id) => {
    const item = page[id];
    const mgr = getManager(item.type);
    const count = (comps[mgr.type]?.count ?? 0) + 1;
    comps[mgr.type] = { count, name: mgr.name, managerType: mgr.managerType };
  });
  const keys = Object.keys(comps).sort((a, b) => {
    const _a = comps[a];
    const _b = comps[b];
    if (_a.managerType === _b.managerType) {
      return _a.name.localeCompare(_b.name);
    }
    if (_a.managerType === 'Action') {
      return 1;
    }
    if (_b.managerType === 'Action') {
      return -1;
    }
    return _a.managerType.localeCompare(_b.managerType);
  });

  const status = useStateXStatus();
  const statusDetails = useStateXStatusDetails();
  const theme = useTheme();
  const { ref, width, height } = useComponentSize();
  const h = Math.round(height / 2);

  return (
    <Dialog onClose={onClose} fullScreen open>
      <DialogTitle sx={{ m: 0, p: 2, pr: '72px' }}>
        About Page - {pageName}
        <Typography
          component="p"
          variant="subtitle2"
          sx={{ color: 'text.secondary' }}
        >
          {ids.length} components using {keys.length} different types, out of
          which ({getTotalMountedCount()}) are mounted. {status()}
        </Typography>
        <IconButton
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
          }}
          onClick={onClose}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <DialogContent ref={ref} sx={{ p: 0, overflow: 'hidden' }}>
        <CodeInput
          showBorders={false}
          darkMode={theme.palette.mode !== 'light'}
          height={h}
          language="json"
          value={keys
            .map((key) => {
              const mounted = getMountedCount(key);
              return mounted
                ? `${comps[key].name}=${comps[key].count} (${mounted})`
                : `${comps[key].managerType === 'Action' ? 'Action ' : ''}${
                    comps[key].name
                  }=${comps[key].count}`;
            })
            .join('\n')}
          width={width}
          disabled
          loadingMessage="Loading data..."
        />
        <CodeInput
          showBorders={false}
          darkMode={theme.palette.mode !== 'light'}
          height={h}
          language="json"
          value={statusDetails()}
          width={width}
          disabled
          loadingMessage="Loading data..."
        />
      </DialogContent>
    </Dialog>
  );
}
